import { Product, Package } from "../types/product.types"
import { error as logError } from "./logger";
import { StorageItem } from "../types/storageItem.types";
import { Activity } from "../types/activity.types";
import { CartData, CartItem } from "../types/cart.types";
import { setItem, updateItem, deleteItem } from "../services/cartService";
import { denormalizeDate } from "./calendar";
import { PastOrder } from "../types/order.types";
import { log } from "./logger";
import { defaultOrderTime } from "../constants/times";
import { toCurrencyAmount } from "./prices";

type CartItemKey = {
    id: number;
    category: string;
    dateKey: string;
    time?: string;
};

export type ProductLookup = {
    allProducts?: Product[];
    packages?: Package[];
    items?: StorageItem[];
    activities?: Activity[];
};

/**
 * Resolves a product/package/storage/activity by cart item category and id.
 */
export const resolveProductByCategory = (
    el: CartItem | { id: number; category: string },
    lookup: ProductLookup
): Product | Package | StorageItem | Activity | undefined => {
    const id = Number(el.id);
    if (el.category === "sales") return lookup.allProducts?.find((p) => p.id === id);
    if (el.category === "package") return lookup.packages?.find((p) => p.id === id);
    if (el.category === "storage") return lookup.items?.find((p) => p.id === id);
    if (el.category === "activities") return lookup.activities?.find((p) => p.id === id);
    return undefined;
};

export const getCartItemForProduct = (
    items: CartData["items"] | undefined,
    key: CartItemKey
): CartItem | undefined => {
    const dateItems = items?.[key.dateKey] ?? [];
    return dateItems.find(
        (item) =>
            item.id === key.id &&
            item.category === key.category &&
            (key.time ? item.time === key.time : true)
    );
};
/**
* Adds, updates, or removes an item in the cart.
* 
* - Adds the item if it does not exist for the given date.
* - Removes the item if amount is 0.
* - Updates the item if it exists.
* 
* @param {ProductInCartType[]} cartData - Current cart data array.
* @param {number} id - ID of the product/item/venue.
* @param {string} date - Date associated with the cart item.
* @param {string} [time] - Optional time slot; defaults to "5:00 - 5:45" if not provided.
* @param {number} [amount] - Optional amount; defaults to 1 when adding new.
* @param {string} [type] - Optional type of item ("product", "venue", "extra"); defaults to "product".
* @param {string} [originalDate] - Optional original date of the cart record
* @param {string} [originalTime] - Optional original time of the cart record
* 
* @returns {ProductInCartType[]} Updated cart data array.
*/

export const cartChange = async (
    cartData: CartData | null,
    id: number,
    date: string,
    time?: string,
    amount?: number,
    type?: string,
    originalDate?: string,
    originalTime?: string,
    onReload?: () => void
) => {
    if (!cartData) {
        logError("No cart data available");
        return;
    }
    const products = cartData;
    if(!products) return
    // Set default time only if it's undefined 
    const finalType = type ?? "product";
    const finalTime = time ?? defaultOrderTime;
    const existingDate = originalDate ?? date;
    const existingTime = originalTime ?? finalTime;

    const sourceItems = products.items[denormalizeDate(existingDate)] ?? [];
    const sourceItem = sourceItems.find(
        item => item.id === id && item.time === existingTime
    );

    // Add new item if it doesnt exist
    if (!sourceItem && amount && amount > 0) {
        const newItem = {
            category: finalType,
            id: id,
            amount: amount ?? 1,
            date: date,
            time: finalTime
        };
        await setItem(newItem)
        onReload?.();
        return
    }


    // Remove product if amount is 0
    if (sourceItem && amount === 0) {
        const deletedItem = {
            category: sourceItem.category,
            id: id,
            date: denormalizeDate(existingDate),
            time: existingTime
        };
        await deleteItem(deletedItem);
        onReload?.();
        return;
    }

    // Check if this is actually a move operation (date or time is changing)
    const isMoving = existingDate !== date || existingTime !== finalTime;

    if (isMoving && sourceItem) {
        await updateItem({ ...sourceItem, date: denormalizeDate(existingDate) }, denormalizeDate(date), finalTime);
        onReload?.();
        return;
    }


    // Update existing product (same location, just updating amount or other properties)
    if (sourceItem) {
        const newAmount= amount ?? sourceItem.amount;
        const updatedItem = {
            category: sourceItem.category,
            id: sourceItem.id,
            amount: newAmount,
            date: date,
            time: existingTime
        };
        await setItem(updatedItem);
        onReload?.();
        return;

    }
    return;
};

/**
* Groups cart data by delivery date and time.
*
* - Iterates through each cart item and organizes them into a nested object structure.
* - The first level of keys represents the delivery date.
* - The second level of keys represents the delivery time.
* - Each value is an array of `ProductInCartType` items scheduled for that date and time.
* - Items without a valid `date` or `time` field are ignored.
*
* @param {ProductInCartType[]} cartData - Array of cart items to be grouped.
*
* @returns {{
*   [date: string]: {
*     [time: string]: ProductInCartType[]
*   }
* }} - Nested object where cart items are grouped by date and then time.
*/
export const groupCartData = (cartData: CartData | null | PastOrder | undefined) => {
    const groupedCart: {
        [date: string]: {
            [time: string]: CartItem[];
        };
    } = {};

    if (!cartData?.items) return groupedCart;

    Object.entries(cartData.items).forEach(([date, items]) => {
        items.forEach(item => {
        const time = item.time;
        if (!time) return; // Skip items with no time

        if (!groupedCart[date]) groupedCart[date] = {};
        if (!groupedCart[date][time]) groupedCart[date][time] = [];

        groupedCart[date][time].push(item);
        });
    });

    return groupedCart;
}

type GroupedCart = Record<string, Record<string, CartItem[]>>;

export const getGroupedBlockCount = (groupedCart?: GroupedCart | null): number => {
    if (!groupedCart) return 0;
    return Object.values(groupedCart).reduce(
        (sum, timesObj) => sum + Object.keys(timesObj).length,
        0
    );
};

type BlockPriceLookup = {
    sales?: Product[];
    packages?: Package[];
    storage?: StorageItem[];
};

export const getCartBlockPrice = (
    items: CartItem[] = [],
    lookup: BlockPriceLookup = {}
): number => {
    return items.reduce((sum, item) => {
        if (item.category !== "sales" && item.category !== "package" && item.category !== "storage") {
            return sum;
        }

        if (typeof item.price_per_piece === "number") {
            return sum + item.price_per_piece * (item.amount ?? 0);
        }

        if (typeof item.price_without_tax === "number") {
            return sum + item.price_without_tax;
        }

        const itemId = Number(item.id);
        if (item.category === "sales") {
            const product = lookup.sales?.find((p) => p.id === itemId);
            return sum + (product?.pricing?.price_without_tax ?? 0) * (item.amount ?? 0);
        }

        if (item.category === "package") {
            const product = lookup.packages?.find((p) => p.id === itemId);
            return sum + (product?.pricing?.price_without_tax ?? 0) * (item.amount ?? 0);
        }

        if (item.category === "storage") {
            const product = lookup.storage?.find((p) => p.id === itemId);
            return sum + (product?.pricing?.price_without_tax ?? 0) * (item.amount ?? 0);
        }

        return sum;
    }, 0);
};

type TransportLookupItem = {
    id: number;
    transport?: boolean;
    // Packages carry their bundled contents; a package that bundles a rental
    // (storage) item needs svoz because that gear has to be collected afterwards.
    items?: { category?: string }[];
};
type TransportLookup = {
    sales?: TransportLookupItem[];
    packages?: TransportLookupItem[];
    storage?: TransportLookupItem[];
};

export const hasTransportForItems = (
    items?: CartData["items"],
    lookup: TransportLookup = {}
): boolean => {
    if (!items) return false;

    const findTransport = (list: TransportLookupItem[] | undefined, id: number) =>
        list?.find((p) => p.id === id)?.transport === true;

    // A package needs svoz if it is explicitly flagged transport:true OR it
    // bundles any rental/storage item that must be collected afterwards.
    const packageNeedsTransport = (id: number) => {
        const pkg = lookup.packages?.find((p) => p.id === id);
        if (!pkg) return false;
        if (pkg.transport === true) return true;
        return (pkg.items ?? []).some((bundled) => bundled.category === "storage");
    };

    return Object.values(items).some((dateItems) =>
        dateItems.some((item) => {
            const itemId = Number(item.id);
            if (item.category === "storage") return findTransport(lookup.storage, itemId);
            if (item.category === "sales") return findTransport(lookup.sales, itemId);
            if (item.category === "package") return packageNeedsTransport(itemId);
            return false;
        })
    );
};

// True if a single block's items contain any product that needs svoz.
export const blockHasSvoz = (
    products: CartItem[] = [],
    lookup: TransportLookup = {}
): boolean => hasTransportForItems({ block: products }, lookup);

// Number of blocks (date+time) that contain at least one svoz product.
// Svoz is charged once per such block.
export const getSvozBlockCount = (
    groupedCart?: GroupedCart | null,
    lookup: TransportLookup = {}
): number => {
    if (!groupedCart) return 0;
    return Object.values(groupedCart).reduce(
        (sum, timesObj) =>
            sum +
            Object.values(timesObj).filter((products) => blockHasSvoz(products, lookup)).length,
        0
    );
};

type CartTotals = {
    withoutTax: number;
    withTax: number;
    totalAmount: number;
};

const parseTaxRate = (rate?: string | null): number | null => {
    if (!rate) return null;
    const value = Number.parseFloat(rate);
    return Number.isFinite(value) ? value / 100 : null;
};

/**
* Calculates totals from cart items using server-provided pricing.
*/
export const getCartTotalsFromItems = (items?: CartData["items"]): CartTotals => {
    let withoutTax = 0;
    let withTax = 0;
    let totalAmount = 0;

    if (!items) return { withoutTax, withTax, totalAmount };

    Object.values(items).forEach((dateItems) => {
        dateItems.forEach((item) => {
            totalAmount += item.amount ?? 0;

            let lineWithoutTax = 0;
            if (typeof item.price_without_tax === "number") {
                lineWithoutTax = item.price_without_tax;
            } else if (typeof item.price_per_piece === "number") {
                lineWithoutTax = item.price_per_piece * item.amount;
            }
            withoutTax += lineWithoutTax;

            let lineWithTax = 0;
            if (typeof item.price_with_tax === "number") {
                lineWithTax = item.price_with_tax;
            } else {
                const rate = parseTaxRate(item.tax_rate);
                lineWithTax = rate != null ? lineWithoutTax * (1 + rate) : lineWithoutTax;
            }
            withTax += lineWithTax;
        });
    });

    return { withoutTax, withTax, totalAmount };
};

// Fallback svoz price (per block) used until the backend sends `collection_item`.
// Must equal the backend payment gate's COLLECTION_FEE_CZK so the shown total
// matches the charged amount.
export const COLLECTION_FEE_CZK = 660;

/**
* Per-block svoz price. Uses the server-provided `collection_item` when present,
* otherwise falls back to COLLECTION_FEE_CZK (no VAT split available).
*/
export const getCollectionUnitPrice = (
    cartData: CartData | null | undefined
): { noTaxCzk: number; withTaxCzk: number } => {
    const item = cartData?.collection_item;
    return {
        noTaxCzk: item?.price_without_tax ?? COLLECTION_FEE_CZK,
        withTaxCzk: item?.price_with_tax ?? COLLECTION_FEE_CZK,
    };
};

type CartPriceSummary = {
    baseNoTaxCzk: number;
    baseWithTaxCzk: number;
    deliveryNoTaxCzk: number;
    deliveryWithTaxCzk: number;
    blocksCount: number;
    collectionNoTaxCzk: number;
    collectionWithTaxCzk: number;
};

export type CartPriceSummaryCurrency = {
    baseNoTax: number;
    baseWithTax: number;
    deliveryNoTax: number;
    deliveryWithTax: number;
    blocksCount: number;
    collectionNoTax: number;
    collectionWithTax: number;
    totalNoTax: number;
    totalWithTax: number;
};

export const getCartPriceSummary = (
    cartData: CartData | null | undefined,
    blocksCount: number,
    svozBlockCount: number
): CartPriceSummary => {
    const itemTotals = getCartTotalsFromItems(cartData?.items);
    const serverNoTax = cartData?.totals?.total_price_without_tax ?? 0;
    const serverWithTax = cartData?.totals?.total_price_with_tax ?? 0;

    const baseNoTaxCzk = itemTotals.withoutTax > 0 ? itemTotals.withoutTax : serverNoTax;
    const baseWithTaxCzk = itemTotals.withTax > 0 ? itemTotals.withTax : serverWithTax;
    const deliveryNoTaxCzk = cartData?.delivery_item?.price_without_tax ?? 0;
    const deliveryWithTaxCzk = cartData?.delivery_item?.price_with_tax ?? 0;
    // Svoz charged once per block that contains a svoz product.
    const svozBlocks = Math.max(0, svozBlockCount);
    const collectionUnit = getCollectionUnitPrice(cartData);

    return {
        baseNoTaxCzk,
        baseWithTaxCzk,
        deliveryNoTaxCzk,
        deliveryWithTaxCzk,
        blocksCount,
        collectionNoTaxCzk: collectionUnit.noTaxCzk * svozBlocks,
        collectionWithTaxCzk: collectionUnit.withTaxCzk * svozBlocks,
    };
};

export const getCartPriceSummaryCurrency = (
    summary: CartPriceSummary,
    currency: string,
    rate: number
): CartPriceSummaryCurrency => {
    const baseNoTax = toCurrencyAmount(summary.baseNoTaxCzk, rate, currency);
    const baseWithTax = toCurrencyAmount(summary.baseWithTaxCzk, rate, currency);
    const deliveryNoTax = toCurrencyAmount(summary.deliveryNoTaxCzk, rate, currency);
    const deliveryWithTax = toCurrencyAmount(summary.deliveryWithTaxCzk, rate, currency);
    const collectionNoTax = toCurrencyAmount(summary.collectionNoTaxCzk, rate, currency);
    const collectionWithTax = toCurrencyAmount(summary.collectionWithTaxCzk, rate, currency);

    return {
        baseNoTax,
        baseWithTax,
        deliveryNoTax,
        deliveryWithTax,
        blocksCount: summary.blocksCount,
        collectionNoTax,
        collectionWithTax,
        totalNoTax: baseNoTax + deliveryNoTax * summary.blocksCount + collectionNoTax,
        totalWithTax: baseWithTax + deliveryWithTax * summary.blocksCount + collectionWithTax,
    };
};
